/*
 * Dashboard.jsx
 * Copyright (c) Harman International Industries, Incorporated, 2024 - 2025
 * All rights reserved.
 */
import { Backdrop, CircularProgress, Container } from "@mui/material";
import PropTypes from "prop-types";
import { useCallback, useEffect, useState } from "react";
import Topheader from "../../../Layout/header/header";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import ApplicationDetailsSideMenu from "../../ApplicationDetailsSideMenu/ApplicationDetailsSideMenu";
import "./Dashboard.css";
import { fetchApplicationDetails } from "../../../Services/redux/appId-details/AppIdDeatilsActions";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { fetchSubmissionDetails } from "../../../Services/redux/Application-details/ApplicationDetailsActions";
import { fetchAllSubmissions } from "../../../Services/redux/ApplicationDetailsSubmissions/ApplicationDetailsSubmissionsActions";
import APIManager from "../../../Services/ApiManager/ApiManager";
import { fetchCountriesDetails } from "../../../Services/redux/Country-details/CountryDetailsActions";
import { StatisticsTrendsCards } from "../charts/StatisticsTrendsCards";
import { PieChartComponent } from "../charts/PieChart";
import { BarChartComponent } from "../charts/BarChart";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import PopupDialog from "../../PopupDialog";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import { format, subDays } from "date-fns";
import { DrawerFilter } from "./DrawerFilter";

const findFullCountry = (countries, countryCode) =>
  countries.find(
    (c) => c.iso3Code === countryCode || c.iso2Code === countryCode
  ) ?? countryCode;

// LoadingBackdrop Component - Reusable loading indicator
const LoadingBackdrop = ({ open }) => (
  <Backdrop
    sx={(theme) => ({ color: "#fff", zIndex: theme.zIndex.drawer + 1 })}
    open={open}
  >
    <CircularProgress color="inherit" />
  </Backdrop>
);

LoadingBackdrop.propTypes = {
  open: PropTypes.bool.isRequired,
};

// Error handler utility
const handleError = (error, setComponentName, setErrorMessage, setHeaderMessage, setOpenErrorPopup, setLoader) => {
  setComponentName("appError");
  setErrorMessage(error.message);
  if (error.message === "jwt expired") {
    setHeaderMessage("401 Token Expired");
  } else {
    setHeaderMessage("Error");
  }
  setOpenErrorPopup(true);
  setLoader(false);
};

export default function Dashboard() {
  const { appId, submissionId } = useParams();
  const [loader, setLoader] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [selectedVersions, setSelectedVersions] = useState([]);
  const [selectedMakes, setSelectedMakes] = useState([]);
  const [selectedCountries, setSelectedCountries] = useState([]);
  const [statisticsTotalData, setStatisticsTotalData] = useState({});
  const [loadStatisticsData, setLoadStatisticsData] = useState(false);
  const [loadBarChartData, setLoadBarChartData] = useState(false);
  const [barAndPieObjData, setBarAndPieObjData] = useState({});

  const [componentName, setComponentName] = useState("");
  const [openErrorPopup, setOpenErrorPopup] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [headerMessage, setHeaderMessage] = useState("");

  const defaultStartDate = subDays(new Date(), 30);
  const currentDate = new Date();
  const currentMonth = new Date(
    currentDate.getFullYear(),
    currentDate.getMonth(),
    1
  );
  const [startDate, setStartDate] = useState(defaultStartDate);
  const [endDate, setEndDate] = useState(currentDate);
  const appData = useSelector(
    (state) => state.applicationDetails.applicationDetails
  );

  const [openFilter, setOpenFilter] = useState(false);
  const submissionData = useSelector(
    (state) => state.submissionDetails.submissionDetails
  );

  const allSubmissionsData = useSelector(
    (state) => state.applicationAllSubmissions.submissions
  );

  const countriesFromRedux = useSelector(
    (state) => state.countryDetails.countries
  );

  const subBinaryData = submissionData?.binary;

  const callNewSubmissionId = (submissionId) => {
    dispatch(fetchSubmissionDetails(appId, submissionId));
  };

  const backToApplications = () => {
    navigate(`/ui/developer/applications/${appData.id}`, {
      state: {
        teamId: sessionStorage.getItem("teamId"),
        submissionId: submissionId,
      },
    });
  };

  const handlePopUpClose = () => {
    setOpenErrorPopup(false);
  };

  const toggleDrawer = (newOpen) => () => {
    setOpenFilter(newOpen);
  };

  const normalizeFilterPayload = (raw = {}) => ({
  selectedVersions: (raw.selectedVersions || []).filter(
    (v) => typeof v === "string" && v.trim() !== ""
  ),
  selectedMakes: (raw.selectedMakes || []).filter(
    (m) => typeof m === "string" && m.trim() !== ""
  ),
  selectedCountries: (raw.selectedCountries || [])
    .map((c) => {
      if (typeof c === "string") return c;
      return c?.iso3Code || c?.iso2Code || "";
    })
    .filter((c) => c !== ""),
});

  const handleFilterApiCalls = (props) => {
    setOpenFilter(false);
    if (subBinaryData?.packageName) {
      const normalized = normalizeFilterPayload(props);
      getDashboardChartData(subBinaryData.packageName, normalized);
    }
  };

  const getDashboardChartData = useCallback(
    async (packageName, data) => {
      setLoader(true);
      try {
        const [installsJson, totalJson] = await Promise.all([
          APIManager.postSellerDashboardInstallsData(
            packageName,
            data,
            format(startDate, "yyyy-MM-dd"),
            format(endDate, "yyyy-MM-dd")
          ),
          APIManager.postSellerDashboardTotalData(
            packageName,
            data,
            format(startDate, "yyyy-MM-dd"),
            format(endDate, "yyyy-MM-dd")
          ),
        ]);
        setBarAndPieObjData(installsJson);
        setLoadBarChartData(true);
        setStatisticsTotalData(totalJson);
        setLoadStatisticsData(true);
        setLoader(false);
      } catch (error) {
        handleError(error, setComponentName, setErrorMessage, setHeaderMessage, setOpenErrorPopup, setLoader);
      }
    },
    [startDate, endDate]
  );

  useEffect(() => {
    if (appId) {
      setLoader(true);
      const fetches = [
        Promise.resolve(dispatch(fetchApplicationDetails(appId))),
        Promise.resolve(dispatch(fetchAllSubmissions(appId))),
        Promise.resolve(dispatch(fetchSubmissionDetails(appId, submissionId))),
        Promise.resolve(dispatch(fetchCountriesDetails())),
      ];
      Promise.allSettled(fetches).finally(() => {
        setLoader(false);
      });
    }
  }, [appId, submissionId, dispatch]);

  useEffect(() => {
    setLoader(true);

    if (
      subBinaryData?.packageName &&
      startDate &&
      endDate &&
      countriesFromRedux.length > 0
    ) {
      APIManager.getSellerDashboardOverviewData(subBinaryData.packageName)
        .then((json) => {
          // Map ISO codes to full country objects
          const fullCountryObjects = json.countries.map((code) =>
            findFullCountry(countriesFromRedux, code)
          );

          setSelectedCountries(fullCountryObjects);
          setSelectedMakes(json.makes);

          const data = {
            selectedVersions: json.app_versions,
            selectedCountries: json.countries, // Keep original ISO codes for API calls
            selectedMakes: json.makes,
          };

          getDashboardChartData(subBinaryData.packageName, data);
        })
        .catch((error) => {
          handleError(error, setComponentName, setErrorMessage, setHeaderMessage, setOpenErrorPopup, setLoader);
        });
    } else {
      setLoader(false);
    }
  }, [
    startDate,
    endDate,
    subBinaryData,
    getDashboardChartData,
    countriesFromRedux,
  ]);

  useEffect(() => {
    if (allSubmissionsData && allSubmissionsData.length > 0) {
      const versions = allSubmissionsData.map((ele) => {
        return ele.versionName;
      });
      setSelectedVersions(versions);
    }
  }, [allSubmissionsData]);

  return (
    <>
      {loader && <LoadingBackdrop open={loader} />}
      <Topheader />

      {appData && Object.keys(appData).length > 0 ? (
        <div className="container-my-release">
          <div
            className="side-menu-my-release"
            style={{ marginTop: "69px", marginLeft: "33px", width: "16%" }}
          >
            <button
              type="button"
              className="arrow-back"
              onClick={backToApplications}
              style={{ background: "none", border: "none", padding: 0 }}
            >
              <div style={{ display: "flex", alignItems: "center" }}>
                <ArrowBackIcon style={{ color: " #717273" }} />
              </div>
              <div className="my-apps">Back to App Details</div>
            </button>
            {appData && Object.keys(appData).length > 0 && (
              <ApplicationDetailsSideMenu
                appData={appData}
                callNewSubmissionId={callNewSubmissionId}
                appMultiSubmissionId={submissionId}
                submissionDetails={submissionData}
              />
            )}
          </div>
          <Container maxWidth="lg">
            <div className="dashboard-section" id="dashboard">
                <div
                  className="listing-header-wrapper-all-release"
                  style={{
                    borderBottom: "1px solid  #12141533",
                    paddingTop: "7px",
                    marginTop: "9px",
                    height: "75px",
                    position: "fixed",
                    zIndex: "999",
                    backgroundColor: "white",
                    width: "877px",
                  }}
                >
                  {submissionData && Object.keys(submissionData).length > 0 && (
                    <div
                      className="app-icon-my-release"
                      style={{ marginLeft: "2px" }}
                    >
                      <div className="icon-name-dashboard">
                        <img
                          className="appIconCls"
                          src={submissionData.icon.url}
                          alt={submissionData.fileName}
                          width={50}
                          height={50}
                        />
                        {
                          <div className="name-status name-status-dashboard">
                            <span className="application-name">
                              {appData.name}
                            </span>
                          </div>
                        }
                        <div className="timeRangeCls">
                          <span className="dateRangeTextCls">Date Range</span>
                          <LocalizationProvider dateAdapter={AdapterDateFns}>
                            <DatePicker
                              label="Start Date"
                              value={startDate}
                              onChange={(newValue) => setStartDate(newValue)}
                              maxDate={currentDate}
                              slotProps={{ textField: { size: "small" } }}
                              views={["year", "month", "day"]}
                            />

                            <DatePicker
                              label="End Date"
                              value={endDate}
                              onChange={(newValue) => setEndDate(newValue)}
                              minDate={startDate || currentMonth}
                              slotProps={{ textField: { size: "small" } }}
                              views={["year", "month", "day"]}
                            />
                          </LocalizationProvider>

                          <FilterAltOutlinedIcon
                            color="action"
                            className="filterIconCls"
                            onClick={toggleDrawer(true)}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div>&nbsp;</div>
                <div style={{ marginTop: "90px" }}>
                  {loadStatisticsData && (
                    <StatisticsTrendsCards
                      statisticsTotalData={statisticsTotalData}
                    />
                  )}
                </div>
                <div>&nbsp;</div>
                <div>
                  {loadBarChartData && (
                    <BarChartComponent
                      barAndPieObjData={barAndPieObjData}
                      startDate={startDate}
                      endDate={endDate}
                    />
                  )}
                </div>
                <div>&nbsp;</div>
                <div>
                  {loadBarChartData && (
                    <PieChartComponent barAndPieObjData={barAndPieObjData} />
                  )}
                </div>
              </div>
            </Container>
        </div>
      ) : (
        <div style={{ marginTop: "100px", textAlign: "center" }}>
          <CircularProgress />
          <p>Loading application data...</p>
        </div>
      )}

      <PopupDialog
        openErrorPopup={openErrorPopup}
        errorMessage={errorMessage}
        headerMessage={headerMessage}
        componentName={componentName}
        onClose={handlePopUpClose}
      />
      <DrawerFilter
        openFilter={openFilter}
        toggleDrawer={toggleDrawer}
        selectedVersions={selectedVersions}
        selectedMakes={selectedMakes}
        selectedCountries={selectedCountries}
        handleFilterApiCalls={handleFilterApiCalls}
      />
    </>
  );
}
